import { Link, useLocation } from 'react-router-dom'
import { cn } from '@/lib/utils'
import {
  LayoutDashboard,
  MessageSquare,
  FileText,
  Users,
  Settings,
  BarChart3,
  Shield,
  HelpCircle,
  Plane,
  Activity,
  Wrench,
  History,
  Search,
  CalendarClock,
  Link2,
  MapPin,
  Radio,
} from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from '@/components/ui/tooltip'

interface NavItem {
  title: string
  href: string
  icon: React.ElementType
  badge?: string
}

const mainNavItems: NavItem[] = [
  { title: 'Dashboard', href: '/app', icon: LayoutDashboard },
  { title: 'Chat', href: '/app/chat', icon: MessageSquare },
  { title: 'Search', href: '/app/search', icon: Search },
  { title: 'Maintenance', href: '/app/maintenance-program', icon: CalendarClock },
  { title: 'Predictive', href: '/app/predictive', icon: Activity },
  { title: 'Troubleshooting', href: '/app/troubleshooting', icon: Wrench },
  { title: 'Aircraft History', href: '/app/aircraft-history', icon: History },
  { title: 'Manuals', href: '/app/manuals', icon: FileText },
]

const adminNavItems: NavItem[] = [
  { title: 'Team', href: '/app/admin/users', icon: Users },
  { title: 'Service Centers', href: '/app/admin/service-centers', icon: MapPin },
  { title: 'Connected Sources', href: '/app/admin/connected-sources', icon: Link2 },
  { title: 'CAMP Integration', href: '/app/admin/camp-integration', icon: Radio },
  { title: 'Analytics', href: '/app/admin/analytics', icon: BarChart3 },
  { title: 'Audit Log', href: '/app/admin/audit', icon: Shield },
]

const bottomNavItems: NavItem[] = [
  { title: 'Settings', href: '/app/settings', icon: Settings },
  { title: 'Help', href: '/app/help', icon: HelpCircle },
]

interface SidebarProps {
  collapsed?: boolean
}

export function Sidebar({ collapsed = false }: SidebarProps) {
  const location = useLocation()

  const NavLink = ({ item }: { item: NavItem }) => {
    const isActive = location.pathname === item.href ||
      (item.href !== '/app' && location.pathname.startsWith(item.href))
    const Icon = item.icon

    const linkContent = (
      <Link
        to={item.href}
        className={cn(
          'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all hover:bg-white/10',
          isActive
            ? 'bg-white/15 text-white'
            : 'text-gray-300 hover:text-white'
        )}
      >
        <Icon className="h-5 w-5 shrink-0" />
        {!collapsed && <span>{item.title}</span>}
        {!collapsed && item.badge && (
          <span className="ml-auto rounded-full bg-voltaire-accent px-2 py-0.5 text-xs text-white">
            {item.badge}
          </span>
        )}
      </Link>
    )

    if (collapsed) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
          <TooltipContent side="right" className="flex items-center gap-2">
            {item.title}
            {item.badge && (
              <span className="rounded-full bg-voltaire-accent px-2 py-0.5 text-xs text-white">
                {item.badge}
              </span>
            )}
          </TooltipContent>
        </Tooltip>
      )
    }

    return linkContent
  }

  return (
    <TooltipProvider>
      <div
        className={cn(
          'flex h-screen flex-col bg-navy-900 text-white transition-all duration-300',
          collapsed ? 'w-16' : 'w-64'
        )}
      >
        {/* Logo */}
        <div className="flex h-16 items-center border-b border-white/10 px-4">
          <Link to="/app" className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-voltaire-blue">
              <Plane className="h-5 w-5 text-white" />
            </div>
            {!collapsed && (
              <div className="flex flex-col">
                <span className="text-lg font-semibold">Voltaire</span>
                <span className="text-xs text-gray-400">Aviation AI</span>
              </div>
            )}
          </Link>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 px-3 py-4">
          <nav className="space-y-1">
            {mainNavItems.map((item) => (
              <NavLink key={item.href} item={item} />
            ))}
          </nav>

          <Separator className="my-4 bg-white/10" />

          {!collapsed && (
            <p className="mb-2 px-3 text-xs font-semibold uppercase tracking-wider text-gray-500">
              Admin
            </p>
          )}
          <nav className="space-y-1">
            {adminNavItems.map((item) => (
              <NavLink key={item.href} item={item} />
            ))}
          </nav>
        </ScrollArea>

        {/* Bottom Navigation */}
        <div className="border-t border-white/10 px-3 py-4">
          <nav className="space-y-1">
            {bottomNavItems.map((item) => (
              <NavLink key={item.href} item={item} />
            ))}
          </nav>
        </div>
      </div>
    </TooltipProvider>
  )
}
